import { useState } from 'react';
import { NavLink, Outlet, Link } from 'react-router';
import Logo from '../brand/Logo.jsx';
import Wordmark from '../brand/Wordmark.jsx';

/**
 * Admin shell. Same ink, same paper, same three typefaces as the storefront —
 * the internals are data-dense and utilitarian, but it should never read as a
 * bolted-on dashboard template. The paper grain is dropped here: it competes
 * with tabular data.
 */
const SECTIONS = [
  { heading: 'Overview', links: [{ to: '/admin', label: 'Dashboard', end: true }] },
  {
    heading: 'Catalog',
    links: [
      { to: '/admin/books/secondhand', label: 'Secondhand books' },
      { to: '/admin/books/publisher', label: 'Publisher books' },
      { to: '/admin/publishers', label: 'Publishers' },
      { to: '/admin/categories', label: 'Categories' },
    ],
  },
  {
    heading: 'Commerce',
    links: [
      { to: '/admin/orders', label: 'Orders' },
      { to: '/admin/subscription-plan', label: 'Subscription plan' },
      { to: '/admin/promo-codes', label: 'Promo codes' },
      { to: '/admin/promotions', label: 'Signup promotions' },
    ],
  },
  {
    heading: 'People',
    links: [
      { to: '/admin/users', label: 'Users' },
      { to: '/admin/audit-log', label: 'Audit log' },
    ],
  },
];

function linkClass({ isActive }) {
  return [
    'block py-1.5 text-sm no-underline transition-colors duration-150',
    isActive ? 'text-stamp-ink font-semibold' : 'text-ink-80 hover:text-ink',
  ].join(' ');
}

export default function AdminLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="bg-paper text-ink min-h-dvh lg:grid lg:grid-cols-[16rem_1fr]">
      {/* Mobile Topbar */}
      <div className="rule-b bg-paper-raised sticky top-0 z-30 flex items-center justify-between px-4 py-3 lg:hidden">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="p-2 -ml-2 text-ink hover:text-stamp-ink focus:outline-none"
            aria-label="Open navigation menu"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <Logo size={22} title="Chapters&Co" />
          <Wordmark className="text-sm" />
          <span className="label text-[11px] text-ink-60">Admin</span>
        </div>
      </div>

      {/* Mobile Drawer Backdrop */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] lg:hidden"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar / Off-canvas Drawer */}
      <aside
        className={`border-rule bg-paper-raised z-50 transition-transform duration-200 ease-in-out lg:translate-x-0 lg:sticky lg:top-0 lg:w-auto lg:h-dvh lg:overflow-y-auto lg:border-r lg:shadow-none flex flex-col ${
          drawerOpen
            ? 'fixed inset-y-0 left-0 w-72 translate-x-0 shadow-2xl'
            : 'fixed inset-y-0 left-0 -translate-x-full'
        }`}
      >
        <div className="rule-b flex items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3">
            <Logo size={26} title="Chapters&Co" />
            <div>
              <Wordmark className="text-base" />
              <span className="label text-ink-60 block">Admin</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            className="p-2 text-ink-60 hover:text-ink lg:hidden"
            aria-label="Close menu"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav aria-label="Admin sections" className="px-6 py-6 flex-1 overflow-y-auto">
          {SECTIONS.map((section) => (
            <div key={section.heading} className="mb-7 last:mb-0">
              <h2 className="label text-ink-60 mb-2">{section.heading}</h2>
              <ul className="list-none space-y-0.5 p-0">
                {section.links.map((link) => (
                  <li key={link.to}>
                    <NavLink
                      to={link.to}
                      end={link.end}
                      className={linkClass}
                      onClick={() => setDrawerOpen(false)}
                    >
                      {link.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="rule-t flex items-center px-6 py-3 shrink-0 mt-auto">
          <Link to="/" className="label text-ink-60 hover:text-ink no-underline">
            &larr; Back to shop
          </Link>
        </div>
      </aside>

      <div className="min-w-0 w-full overflow-x-auto">
        <main id="main" className="px-4 py-6 sm:px-6 sm:py-8 lg:px-8 xl:px-10 lg:py-8 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
